import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import useGetUserMe from "@/hooks/useGetUserMe";
import { useRouter } from "next/router";
import { renderNoData } from "./RenderQ";
import { z } from "zod";
import { useEffect, useState } from "react";
import { Form, useFormHook, ErrMsg } from "@/components/FormComps";
import Space from "./ui/Space";
import { En, Fa } from "./ui/multilang";
import Snack from "./ui/Snack";
import Modal from "./ui/modal";
import Button from "./ui/button";
import Checkbox from "./ui/Checkbox";
import RegisterNewStudents from "./RegisterNewStudents";
import { sendGTMEvent } from "@next/third-parties/google";

const queryKey = "courseIntroductory-1";
const tableName = "course";

export const schema = z.object({
  topicId: z.string().min(1, "Please enter topic ID here"),
  teacherId: z.string().min(1, "Please enter teacher ID here"),
  requestedSessionDurationInMinute: z.string().min(1, "Please enter class duration in minutes"),
  requestMessage: z.string().optional().nullable(),
  requestDate: z.string().min(1, "Please enter request date"),
  payerId: z.string().min(1, "Please enter payer ID"),
  studentId: z.array(z.string()).nonempty({ message: "Can't be empty" }),
});

const IntroductoryMeetingModal = ({ courseQ, profileId, prevCourseQ }) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { locale } = router;
  const supabase = useSupabaseClient();
  const userMeQ = useGetUserMe();
  const [isAddStudentFormPresent, setIsStudentFormPresent] = useState(false);
  const [warning, setWarning] = useState("");

  const studentQ = useQuery({
    queryKey: ["introductoryMeetingRequestStudents-1"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student")
        .select(`id, payerId, first_name, last_name`)
        .eq("payerId", userMeQ.data?.payer.id);
      if (error) throw error;
      return data;
    },
    enabled: !!userMeQ.data?.payer.id,
  });

  const now = new Date();
  const dateTimeString = now.toISOString().substring(0, 16);

  const [addedRecord, setAddedRecord] = useState(null);
  const onSubmit = async ({ studentId, ...formValues }) => {
    sendGTMEvent({ event: "submit-intro-request-modal-click" });
    const correctFormValues = {
      ...formValues,
      requestMessage: formValues?.requestMessage === "" ? null : formValues?.requestMessage,
      //rateOverwrite: formValues?.rateOverwrite === "" ? null : formValues?.rateOverwrite,
      numberOfStudents: studentId.length,
      requestDate: formValues?.requestDate ? new Date(formValues?.requestDate) : null,
      updated_at: new Date(Date.now()),
      updated_by: userMeQ.data?.payer.id,
    };
    const { data, error } = await supabase.from(tableName).insert(correctFormValues).select().single();
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: [queryKey] });
    await Promise.all(
      studentId.map(async (id) => {
        const { data: csData, error: csError } = await supabase
          .from("courseStudent")
          .insert({ courseId: data.id, studentId: id, payerId: data.payerId })
          .select()
          .single();
        if (csError) throw csError;
      })
    );
    setAddedRecord(data);
    queryClient.invalidateQueries({ queryKey: ["bookingPrevCourses-2", profileId] });
    const newUrl = window.location.pathname + "?intro_meeting_requested=true";
    window.history.pushState({}, "", newUrl);

    const sendMailResponse = await fetch("/api/notify-tutor-intro-meeting", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        courseId: data.id,
        requestMessage: formValues?.requestMessage,
        teacherId: courseQ.data.teacherId,
        students: studentQ
          .data!.filter((std) => studentId.includes(std.id))
          .map((s) => s.first_name + " " + s.last_name)
          .join(", "),
        payerName: userMeQ.data.firstname + " " + userMeQ.data.lastname,
        payerId: userMeQ.data.id,
      }),
    });

    const sendMailResult = await sendMailResponse.json();

    if (!sendMailResponse.ok) {
      console.error(sendMailResult.error.message);
    }
  };

  const form = useFormHook({ schema, onSubmit });
  const { watch } = form;

  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().split("T")[0];

  const selectedStudents = watch("studentId");
  const sessionDuration = watch("requestedSessionDurationInMinute");

  useEffect(() => {
    const isDuplicate = prevCourseQ.data?.some((course) => {
      const courseStudents = course.courseStudent.map((cs) => cs.student.id).sort();
      const selectedSorted = [...(selectedStudents || [])].sort();

      return (
        sessionDuration == course.requestedSessionDurationInMinute &&
        JSON.stringify(courseStudents) === JSON.stringify(selectedSorted)
      );
    });

    if (isDuplicate) {
      setWarning(
        locale === "fa"
          ? "این درخواست قبلا از سمت شما برای معلم فرستاده شده. آیا مایلید دوباره با ایشان جلسه معرفی داشته باشید؟"
          : "Your request was previously sent to the tutor. Would you like to have an introductory meeting with him again?"
      );
    } else {
      setWarning("");
    }
  }, [selectedStudents, sessionDuration, prevCourseQ.data]);

  return (
    <div>
      <h2 className="H3">{locale === "fa" ? "جلسه‌ی معرفی رایگان" : "Free Introductory Session"}</h2>
      <div className="flex justify-center">
        {renderNoData(courseQ) ?? renderNoData(userMeQ) ?? renderNoData(studentQ) ?? renderNoData(prevCourseQ) ?? (
          <div className="w-full">
            <Space size="h-4" />
            {!addedRecord && (
              <>
                {prevCourseQ.data.length > 0 && (
                  <>
                    <div className="bg-white rounded-lg p-4 border mb-2">
                      <h3 className="H4 c-sand11">
                        {locale === "fa" ? "لیست جلسات معرفی گذشته" : "Previous Introductory Sessions"}
                      </h3>
                      <ul>
                        {prevCourseQ.data.map((course) => (
                          <li key={course.id} className="flex justify-between rounded-md p-2 my-2 border bg-sand1">
                            <p className="c-sand11">
                              Students:{" "}
                              <span className="font-medium">
                                {course.courseStudent
                                  .map((cs) => cs.student.first_name + " " + cs.student.last_name)
                                  .join(", ")}
                              </span>
                            </p>
                            <p className="c-sand11">
                              Duration:{" "}
                              <span className="font-medium">{course.requestedSessionDurationInMinute} min</span>
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Space size="h-4" />
                  </>
                )}
                <Form form={form} onSubmit={onSubmit} className="" hasSubmitButton={false}>
                  <div className="hidden">
                    <Form.Input type="hidden" name="topicId" value={courseQ.data.topicId} required />
                    <Form.Input type="hidden" name="teacherId" value={courseQ.data.teacherId} required />
                    <Form.Input type="hidden" name="payerId" value={userMeQ.data.payer.id} required />
                    <Form.Input type="hidden" name="requestDate" value={dateTimeString} required />
                    <Form.Input
                      type="hidden"
                      name="requestedSessionDurationInMinute"
                      value={
                        courseQ.data.teacher.expertise.find(
                          (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
                        ).sessionDurationOnWebsiteInMinute
                      }
                      required
                    />
                  </div>

                  <fieldset>
                    <legend className="c-sand11 fw-500">
                      <En>Which student(s) will participate in this class?</En>
                      <Fa>کدام شاگردان در این کلاس شرکت می‌کنند؟</Fa>
                      <sup className="c-red11"> *</sup>
                    </legend>
                    <Space size="h-3" />

                    <label>
                      <input
                        {...form.register("studentId")}
                        type="checkbox"
                        name="studentId"
                        value={null}
                        disabled
                        className="inline hidden"
                      />
                    </label>
                    {studentQ.data!.map((student) => {
                      const isSingleStudent = studentQ.data!.length === 1;
                      return (
                        <>
                          <Checkbox
                            label={student.first_name + " " + student.last_name}
                            {...form.register("studentId")}
                            name="studentId"
                            value={student.id}
                            className="inline"
                            defaultChecked={isSingleStudent}
                          />
                        </>
                      );
                    })}
                  </fieldset>

                  <Space size="h-3" />
                  <Form.TextArea
                    rows={4}
                    name="requestMessage"
                    label={
                      locale === "fa" ? "اگر پیام یا توضیحی دارید اینجا بنویسید" : "Add any notes or explanations here"
                    }
                  />
                  <ErrMsg name="studentId" />
                  {warning && <Snack variant="warning">{warning}</Snack>}
                  <Form.ServerErrorMessage />

                  <Form.SubmitButton width="parent" className="submit-intro-request-modal-button">
                    {locale === "fa" ? "ثبت درخواست" : "Submit"}
                  </Form.SubmitButton>
                </Form>
                <button
                  type="button"
                  onClick={() => {
                    setIsStudentFormPresent((prev) => !prev);
                    sendGTMEvent({ event: "add-student-request-modal-click" });
                  }}
                  className="fw-500 c-prm11 hover:c-prm9 block underline my-2 w-full text-center add-student-button"
                >
                  <En>{isAddStudentFormPresent ? "Close Form" : "Add new Student"}</En>
                  <Fa>{isAddStudentFormPresent ? "بستن فرم" : "اضافه کردن شاگرد دیگر"}</Fa>
                </button>
                {isAddStudentFormPresent && <RegisterNewStudents />}
              </>
            )}

            {addedRecord && (
              <div>
                <Snack variant="success">
                  <p className="">
                    {locale === "fa" ? "درخواست شما با موفقیت ثبت شد." : "Your request is successfully submitted."}
                  </p>
                  <p>
                    <En>
                      Soon, the tutor will contact you on WhatsApp to coordinate the time of the introductory meeting.
                    </En>
                    <Fa>معلم به‌زودی در واتساپ با شما تماس خواهد گرفت.</Fa>
                  </p>
                </Snack>
                <Space size="h-3"></Space>
                <Modal.Close className="w-full">
                  <Button variation="ghost" width="parent">
                    <En>Ok, Got it.</En>
                    <Fa>باشه.</Fa>
                  </Button>
                </Modal.Close>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntroductoryMeetingModal;
